import { useState, useEffect } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  Bell, Mail, MessageCircle, Smartphone, Send, Trash2,
  Plus, X, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp,
  Eye, EyeOff, Loader2, ChevronLeft, ChevronRight, Pencil, Rocket, Gift,
} from "lucide-react";
import CampaignImageField from "../../components/CampaignImageField";

const OPTIN_VARIABLES = [
  { tag: "{{nombre}}", desc: "Nombre del usuario" },
  { tag: "{{correo}}", desc: "Su correo" },
  { tag: "{{puntos}}", desc: "QPoints de premio configurados" },
  { tag: "{{enlace}}", desc: "Enlace personalizado (además del botón)" },
];

const RUN_STATUS_FILTERS = [
  { id: undefined, label: "Todos" },
  { id: "sent",    label: "Enviados" },
  { id: "failed",  label: "Fallidos" },
];

const RUN_RECIPIENTS_PAGE_SIZE = 20;

function WhatsappOptinRunRecipients({ runId }) {
  const [filter, setFilter]       = useState(undefined);
  const [page, setPage]           = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [loading, setLoading]     = useState(true);

  useEffect(() => { setPage(1); }, [filter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: RUN_RECIPIENTS_PAGE_SIZE, page };
    if (filter !== undefined) params.status = filter;
    API.get(`/whatsapp-optin/runs/${runId}/recipients`, { params })
      .then((res) => {
        if (cancelled) return;
        setRecipients(res.data?.data?.data ?? []);
        setTotal(res.data?.data?.total ?? 0);
        setPages(res.data?.data?.pages ?? 1);
      })
      .catch(() => { if (!cancelled) toast.error("Error al cargar los destinatarios"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId, filter, page]);

  return (
    <div className="mt-3 bg-gray-50 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-100 w-fit">
          {RUN_STATUS_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                filter === f.id ? "bg-supernova-100 text-supernova-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{total} destinatario{total !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-gray-300" /></div>
      ) : recipients.length === 0 ? (
        <p className="text-center text-xs text-gray-400 py-4">Nadie en esta categoría</p>
      ) : (
        <div className="rounded-lg border border-gray-100 bg-white overflow-hidden overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Usuario</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Email</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Estado envío</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">¿Agregó WhatsApp?</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5 font-medium text-gray-700">{r.user?.name ?? "—"}</td>
                  <td className="px-3 py-1.5 text-gray-500">{r.email}</td>
                  <td className="px-3 py-1.5">
                    {r.status === "sent" ? (
                      <span className="flex items-center gap-1 text-green-600 font-semibold"><CheckCircle size={11} /> Enviado</span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500 font-semibold" title={r.error ?? ""}>
                        <AlertCircle size={11} /> Falló
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.user?.phone ? (
                      <span className="flex items-center gap-1 text-green-600 font-semibold"><Eye size={11} /> Sí</span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400"><EyeOff size={11} /> No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && pages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-xs text-gray-400">Página {page} de {pages}</span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Siguiente <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function WhatsappOptinRunsPanel() {
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRunId, setOpenRunId] = useState(null);

  useEffect(() => {
    API.get("/whatsapp-optin/runs")
      .then((res) => setRuns(res.data?.data?.data ?? []))
      .catch(() => toast.error("Error al cargar el historial de envíos"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-300" /></div>;
  }
  if (runs.length === 0) {
    return <p className="text-center text-xs text-gray-400 py-4">Todavía no se envió ninguna campaña</p>;
  }

  return (
    <div className="space-y-2">
      {runs.map((run) => (
        <div key={run.runId} className="bg-gray-50 rounded-xl p-3">
          <button
            onClick={() => setOpenRunId((id) => (id === run.runId ? null : run.runId))}
            className="w-full flex items-center justify-between gap-2 text-left"
          >
            <div>
              <p className="text-xs font-semibold text-gray-700">
                {new Date(run.createdAt).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" })}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                <span className="text-green-600 font-semibold">{run.sent} enviados</span>
                {run.failed > 0 && <span className="text-red-500 font-semibold"> · {run.failed} fallidos</span>}
                {" "}de {run.total}
              </p>
            </div>
            {openRunId === run.runId ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
          </button>
          {openRunId === run.runId && <WhatsappOptinRunRecipients runId={run.runId} />}
        </div>
      ))}
    </div>
  );
}

function WhatsappOptinCard() {
  const [count, setCount]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showRuns, setShowRuns] = useState(false);

  const [template, setTemplate]       = useState({ subject: "", body: "", imageUrl: "" });
  const [loadingTpl, setLoadingTpl]   = useState(true);
  const [savingTpl, setSavingTpl]     = useState(false);
  const [testEmail, setTestEmail]     = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const loadCount = () => {
    setLoading(true);
    API.get("/whatsapp-optin/eligible-count")
      .then((res) => setCount(res.data?.data?.count ?? 0))
      .catch(() => toast.error("Error consultando usuarios sin WhatsApp"))
      .finally(() => setLoading(false));
  };

  const loadTemplate = () => {
    setLoadingTpl(true);
    API.get("/whatsapp-optin/template")
      .then((res) => setTemplate({
        subject:  res.data?.data?.subject ?? "",
        body:     res.data?.data?.body ?? "",
        imageUrl: res.data?.data?.imageUrl ?? "",
      }))
      .catch(() => toast.error("Error cargando la plantilla"))
      .finally(() => setLoadingTpl(false));
  };

  useEffect(() => { loadCount(); loadTemplate(); }, []);

  const handleSend = async () => {
    if (!count) return;
    if (!window.confirm(`¿Enviar el correo a los ${count} usuarios sin WhatsApp registrado? Se manda de a poco (~1.5s entre cada uno) para no ser bloqueado por Gmail, así que con muchos usuarios puede tardar bastante — el enlace de cada uno es personalizado, sin necesidad de login.`)) return;
    setSending(true);
    try {
      const res = await API.post("/whatsapp-optin/send");
      const { total, started } = res.data?.data ?? {};
      if (started) {
        toast.success(`Envío iniciado en segundo plano para ${total} usuarios — puede tardar. El resultado final queda en los logs del servidor.`);
      } else {
        toast.info("No hay usuarios elegibles para enviar");
      }
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo enviar la campaña");
    } finally { setSending(false); }
  };

  const handleSaveTemplate = async () => {
    if (!template.subject.trim() || !template.body.trim()) return toast.warn("Completá asunto y mensaje");
    setSavingTpl(true);
    try {
      await API.put("/whatsapp-optin/template", template);
      toast.success("Plantilla guardada");
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo guardar la plantilla");
    } finally { setSavingTpl(false); }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) return toast.warn("Ingresá un correo para la prueba");
    setSendingTest(true);
    try {
      await API.post("/whatsapp-optin/test-email", { email: testEmail.trim(), ...template });
      toast.success(`Correo de prueba enviado a ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo enviar el correo de prueba");
    } finally { setSendingTest(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-xl">
            <MessageCircle size={20} className="text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
              Pedir WhatsApp por email <Gift size={13} className="text-supernova-500" />
            </h3>
            <p className="text-sm text-gray-500">
              {loading ? "Consultando..." : `${count ?? 0} usuarios sin WhatsApp registrado`} — enlace personalizado, sin login, con premio en QPoints.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowRuns((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-xl transition"
          >
            <Eye size={13} /> {showRuns ? "Ocultar historial" : "Ver historial"}
          </button>
          <button
            onClick={() => setEditing((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-xl transition"
          >
            <Pencil size={13} /> {editing ? "Ocultar mensaje" : "Editar mensaje"}
          </button>
          <button
            onClick={handleSend}
            disabled={sending || loading || !count}
            className="flex items-center gap-2 bg-supernova-500 hover:bg-supernova-600 disabled:opacity-50 text-black font-semibold px-4 py-2 rounded-xl text-sm transition"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Enviando..." : "Enviar campaña"}
          </button>
        </div>
      </div>

      {editing && (
        loadingTpl ? (
          <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
        ) : (
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Asunto</label>
              <input
                value={template.subject}
                onChange={(e) => setTemplate((t) => ({ ...t, subject: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Mensaje</label>
              <textarea
                value={template.body}
                onChange={(e) => setTemplate((t) => ({ ...t, body: e.target.value }))}
                rows={4}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300 resize-none"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {OPTIN_VARIABLES.map((v) => (
                <span
                  key={v.tag}
                  title={v.desc}
                  className="text-[11px] font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-md cursor-help"
                >
                  {v.tag}
                </span>
              ))}
            </div>

            <CampaignImageField
              value={template.imageUrl}
              onChange={(url) => setTemplate((t) => ({ ...t, imageUrl: url }))}
              label="Imagen del email (opcional)"
            />

            <div className="flex gap-2">
              <button
                onClick={handleSaveTemplate}
                disabled={savingTpl}
                className="px-3 py-1.5 bg-supernova-500 hover:bg-supernova-600 text-black text-xs font-bold rounded-lg transition disabled:opacity-50"
              >
                {savingTpl ? "Guardando..." : "Guardar plantilla"}
              </button>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1">
                <Mail size={11} /> Probar el correo antes de enviar
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
                />
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={sendingTest}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition disabled:opacity-50 whitespace-nowrap"
                >
                  {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Enviar prueba
                </button>
              </div>
            </div>
          </div>
        )
      )}

      {showRuns && (
        <div className="pt-4 border-t border-gray-100">
          <WhatsappOptinRunsPanel />
        </div>
      )}
    </div>
  );
}

const CHANNEL_CONFIG = {
  push:      { label: "Push",      icon: Smartphone,    color: "bg-purple-100 text-purple-700"  },
  inapp:     { label: "In-app",    icon: Bell,          color: "bg-indigo-100 text-indigo-700"  },
  email:     { label: "Email",     icon: Mail,          color: "bg-blue-100 text-blue-700"      },
  whatsapp:  { label: "WhatsApp",  icon: MessageCircle, color: "bg-green-100 text-green-700"    },
  sms:       { label: "SMS",       icon: Smartphone,    color: "bg-orange-100 text-orange-700"  },
};

const ROLE_OPTIONS = [
  { value: "",        label: "Todos los usuarios"  },
  { value: "user",    label: "Solo clientes"        },
  { value: "company", label: "Solo empresas"        },
  { value: "ryder",   label: "Solo ryders"          },
  { value: "admin",   label: "Solo admins"          },
];

const SEGMENT_LABELS = {
  todos:     "Todos los usuarios",
  inactivos: "Inactivos",
  nuevos:    "Nuevos sin pedido",
};

// Broadcasts (draft/sending/sent/failed) y campañas de Marketing (borrador/pendiente_.../enviada/fallida)
// usan enums de estado distintos — cada fuente tiene su propio mapa de badges.
const STATUS_CONFIG = {
  draft:   { label: "Borrador",  icon: Clock,         cls: "bg-gray-100 text-gray-600"    },
  sending: { label: "Enviando",  icon: Clock,         cls: "bg-yellow-100 text-yellow-700" },
  sent:    { label: "Enviado",   icon: CheckCircle,   cls: "bg-green-100 text-green-700"  },
  failed:  { label: "Fallido",   icon: AlertCircle,   cls: "bg-red-100 text-red-600"      },
};

const MARKETING_STATUS_CONFIG = {
  borrador:             { label: "Borrador",                    icon: Clock,       cls: "bg-gray-100 text-gray-600"    },
  pendiente_aprobacion: { label: "Pendiente de aprobación",      icon: Clock,       cls: "bg-yellow-100 text-yellow-700" },
  pendiente_edicion:    { label: "Esperando edición",            icon: Clock,       cls: "bg-violet-100 text-violet-700" },
  aprobada:             { label: "Aprobada",                     icon: CheckCircle, cls: "bg-blue-100 text-blue-700"    },
  rechazada:            { label: "Rechazada",                    icon: AlertCircle, cls: "bg-red-100 text-red-500"      },
  enviada:              { label: "Enviado",                      icon: CheckCircle, cls: "bg-green-100 text-green-700"  },
  fallida:              { label: "Fallido",                      icon: AlertCircle, cls: "bg-red-100 text-red-600"      },
};

function ChannelBadge({ ch }) {
  const cfg = CHANNEL_CONFIG[ch];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

function StatusBadge({ status, source }) {
  const map = source === "marketing" ? MARKETING_STATUS_CONFIG : STATUS_CONFIG;
  const cfg = map[status] ?? STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

function SourceBadge({ source }) {
  if (source !== "marketing") return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-supernova-100 text-supernova-700">
      <Rocket size={11} /> Marketing
    </span>
  );
}

function ResultsDetail({ results }) {
  const [open, setOpen] = useState(false);
  if (!results) return null;
  const entries = Object.entries(results);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
      >
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        Ver resultados
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {entries.map(([ch, r]) => (
            <div key={ch} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1">
              <span className="font-medium capitalize w-20">{ch}</span>
              <span className="text-green-600">✓ {r.sent} enviados</span>
              {r.failed > 0 && <span className="text-red-500">✗ {r.failed} fallidos</span>}
              {r.error && <span className="text-orange-500 truncate max-w-xs">{r.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RECIPIENT_FILTERS = [
  { id: undefined, label: "Todos" },
  { id: true,      label: "Vieron" },
  { id: false,     label: "No vieron" },
];

const RECIPIENTS_PAGE_SIZE = 20;

function NotifRecipients({ endpoint }) {
  const [filter, setFilter] = useState(undefined);
  const [page, setPage] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [filter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: RECIPIENTS_PAGE_SIZE, page };
    if (filter !== undefined) params.read = filter;
    API.get(`${endpoint}/recipients`, { params })
      .then((res) => {
        if (cancelled) return;
        setRecipients(res.data?.data?.data ?? []);
        setTotal(res.data?.data?.total ?? 0);
        setPages(res.data?.data?.pages ?? 1);
      })
      .catch(() => { if (!cancelled) toast.error("Error al cargar los destinatarios"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint, filter, page]);

  return (
    <div className="mt-3 bg-gray-50 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-100 w-fit">
          {RECIPIENT_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                filter === f.id ? "bg-supernova-100 text-supernova-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{total} destinatario{total !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-gray-300" /></div>
      ) : recipients.length === 0 ? (
        <p className="text-center text-xs text-gray-400 py-4">Nadie en esta categoría</p>
      ) : (
        <div className="rounded-lg border border-gray-100 bg-white overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Usuario</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Email</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-500">Estado</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-1.5 font-medium text-gray-700">{r.user?.name ?? "—"}</td>
                  <td className="px-3 py-1.5 text-gray-500">{r.user?.email ?? "—"}</td>
                  <td className="px-3 py-1.5">
                    {r.read ? (
                      <span className="flex items-center gap-1 text-green-600 font-semibold"><Eye size={11} /> Vio</span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400"><EyeOff size={11} /> No vio</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && pages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-xs text-gray-400">Página {page} de {pages}</span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Siguiente <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function ViewStats({ item, endpoint }) {
  const [open, setOpen] = useState(false);
  if (!item.deliveredCount) return null;
  const pct = item.openRate != null ? Math.round(item.openRate * 100) : 0;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800"
      >
        <Eye size={12} className="text-gray-400" />
        {item.viewedCount}/{item.deliveredCount} vieron la notificación ({pct}%)
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && <NotifRecipients endpoint={endpoint} />}
    </div>
  );
}

const RESEND_CHANNELS = ["push", "email", "whatsapp"];

function ResendBar({ endpoint, onDone }) {
  const [resendingChannel, setResendingChannel] = useState(null);

  const handleResend = async (channel) => {
    setResendingChannel(channel);
    try {
      await API.post(`${endpoint}/resend`, { channel });
      toast.success(`Reenviado por ${CHANNEL_CONFIG[channel]?.label ?? channel}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo reenviar");
    } finally { setResendingChannel(null); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-gray-100">
      <span className="text-xs font-semibold text-gray-500">Reenviar por:</span>
      {RESEND_CHANNELS.map((ch) => {
        const cfg = CHANNEL_CONFIG[ch];
        const Icon = cfg.icon;
        return (
          <button
            key={ch}
            onClick={() => handleResend(ch)}
            disabled={resendingChannel !== null}
            className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 hover:border-supernova-400 text-gray-700 text-xs font-semibold rounded-lg transition disabled:opacity-50"
          >
            {resendingChannel === ch ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}
            {cfg.label}
          </button>
        );
      })}
    </div>
  );
}

const EMPTY_FORM = { title: "", message: "", channels: ["push"], targetRole: "" };

export default function AdminNotifications() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [marketing, setMarketing]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [editingId, setEditingId]   = useState(null);

  const load = async () => {
    try {
      const [broadcastsRes, marketingRes] = await Promise.all([
        API.get("/broadcasts"),
        API.get("/marketing/campaigns", { params: { limit: 50 } }),
      ]);
      setBroadcasts(broadcastsRes.data?.data?.data ?? []);
      setMarketing(marketingRes.data?.data?.data ?? []);
    } catch { toast.error("Error cargando notificaciones"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Une broadcasts y campañas de marketing en una sola bandeja, ordenada por fecha —
  // el admin quiere ver en un solo lugar todo lo que se le mandó a los usuarios.
  const items = [
    ...broadcasts.map((b) => ({ ...b, _source: "broadcast", message: b.message, audienceLabel: ROLE_OPTIONS.find((o) => o.value === (b.targetRole ?? ""))?.label ?? b.targetRole })),
    ...marketing.map((c) => ({ ...c, _source: "marketing", message: c.body, audienceLabel: SEGMENT_LABELS[c.segment] ?? c.segment })),
  ].sort((a, b) => new Date(b.createdAt ?? b.date ?? 0) - new Date(a.createdAt ?? a.date ?? 0));

  const toggleChannel = (ch) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(ch)
        ? f.channels.filter((c) => c !== ch)
        : [...f.channels, ch],
    }));
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleEdit = (b) => {
    setForm({ title: b.title, message: b.message, channels: b.channels ?? ["push"], targetRole: b.targetRole ?? "" });
    setEditingId(b.id);
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) return toast.warn("Completa título y mensaje");
    if (form.channels.length === 0) return toast.warn("Selecciona al menos un canal");
    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/broadcasts/${editingId}`, form);
        toast.success("Broadcast actualizado");
      } else {
        await API.post("/broadcasts", form);
        toast.success("Broadcast creado");
      }
      closeForm();
      load();
    } catch (err) { toast.error(err.response?.data?.message || (editingId ? "Error actualizando broadcast" : "Error creando broadcast")); }
    finally { setSaving(false); }
  };

  const handleSend = async (id) => {
    if (!window.confirm("¿Enviar este broadcast ahora?")) return;
    setSending(id);
    try {
      await API.post(`/broadcasts/${id}/send`);
      toast.success("Broadcast enviado correctamente");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error enviando broadcast");
    } finally { setSending(null); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("¿Eliminar esta notificación?")) return;
    const endpoint = item._source === "marketing" ? `/marketing/campaigns/${item.id}` : `/broadcasts/${item.id}`;
    try {
      await API.delete(endpoint);
      toast.success("Eliminado");
      if (item._source === "marketing") setMarketing((m) => m.filter((x) => x.id !== item.id));
      else setBroadcasts((b) => b.filter((x) => x.id !== item.id));
    } catch { toast.error("Error eliminando"); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-supernova-100 rounded-xl">
            <Bell size={22} className="text-supernova-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Notificaciones masivas</h1>
            <p className="text-sm text-gray-500">Broadcasts y campañas de Marketing enviadas a los usuarios</p>
          </div>
        </div>
        <button
          onClick={() => (showForm ? closeForm() : setShowForm(true))}
          className="flex items-center gap-2 bg-supernova-500 hover:bg-supernova-600 text-black font-semibold px-4 py-2 rounded-xl text-sm transition"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          {showForm ? "Cancelar" : "Nueva notificación"}
        </button>
      </div>

      <WhatsappOptinCard />

      {/* Formulario */}
      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4"
        >
          <h2 className="font-semibold text-gray-700">{editingId ? "Editar broadcast" : "Crear broadcast"}</h2>

          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ej: ¡Nueva promoción disponible!"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-supernova-400 outline-none"
            />
          </div>

          {/* Mensaje */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Escribe el contenido del mensaje..."
              rows={4}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-supernova-400 outline-none resize-none"
            />
          </div>

          {/* Canales */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Canales de envío</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CHANNEL_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = form.channels.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleChannel(key)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium border-2 transition ${
                      active
                        ? "border-supernova-400 bg-supernova-50 text-supernova-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <Icon size={14} />
                    {cfg.label}
                    {active && <CheckCircle size={12} className="text-supernova-600" />}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              SMS requiere proveedor externo · WhatsApp requiere servidor library activo
            </p>
          </div>

          {/* Destinatarios */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destinatarios</label>
            <select
              value={form.targetRole}
              onChange={(e) => setForm((f) => ({ ...f, targetRole: e.target.value }))}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-supernova-400 outline-none"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-supernova-500 hover:bg-supernova-600 disabled:opacity-60 text-black font-semibold py-2.5 rounded-xl text-sm transition"
          >
            {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Guardar broadcast"}
          </button>
        </form>
      )}

      {/* Lista — une broadcasts y campañas de Marketing enviadas a los usuarios */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Bell size={40} className="mx-auto mb-3 opacity-30" />
          <p>No hay notificaciones creadas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const endpoint = item._source === "marketing" ? `/marketing/campaigns/${item.id}` : `/broadcasts/${item.id}`;
            const isSent   = item._source === "marketing" ? item.status === "enviada" : item.status === "sent";
            const isDraft  = item._source === "marketing" ? item.status === "borrador" : item.status === "draft";
            return (
              <div
                key={`${item._source}-${item.id}`}
                className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <StatusBadge status={item.status} source={item._source} />
                      <SourceBadge source={item._source} />
                      {item.audienceLabel && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {item.audienceLabel}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-800 truncate">{item.title}</h3>
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{item.message}</p>

                    {/* Canales */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(item.channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} />)}
                    </div>

                    {/* Resultados */}
                    <ResultsDetail results={item.results} />

                    {/* Vistas reales (basado en Notification.read) */}
                    <ViewStats item={item} endpoint={endpoint} />

                    {/* Reenviar por otro canal */}
                    {isSent && <ResendBar endpoint={endpoint} onDone={load} />}

                    {item._source === "marketing" && !isSent && (
                      <p className="text-xs text-gray-400 mt-2 italic">Se gestiona desde la pestaña Marketing</p>
                    )}

                    {item.sentAt && (
                      <p className="text-xs text-gray-400 mt-2">
                        Enviado: {new Date(item.sentAt).toLocaleString("es-CO")}
                      </p>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-2 shrink-0">
                    {item._source === "broadcast" && isDraft && (
                      <>
                        <button
                          onClick={() => handleSend(item.id)}
                          disabled={sending === item.id}
                          className="flex items-center gap-1.5 bg-supernova-500 hover:bg-supernova-600 disabled:opacity-60 text-black text-xs font-semibold px-3 py-2 rounded-xl transition"
                        >
                          <Send size={13} />
                          {sending === item.id ? "Enviando..." : "Enviar"}
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition"
                          title="Editar"
                        >
                          <Pencil size={15} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
                      title="Eliminar"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
